import {
  Level,
  LevelUpSession,
  AdvancementChoice,
  ExperienceEntry,
  MarkedTraits,
  TierAdvancementState,
  LevelUpChanges,
  increaseExperience,
  markTraits,
  clearMarkedTraits,
  createEmptyMarkedTraits,
  getTierForLevel
} from '../types/levelUp';
import { CharacterData, ContentReference } from '../types/character';
import { extractLevelUpChanges } from './levelUpCalculations';
import { buildMulticlassData } from '../utils/multiclassUtils';

// ============================================================================
// CHARACTER UPDATE FUNCTIONS
// ============================================================================

/**
 * Applies a completed level-up session to a character
 * Returns updated character data (immutable)
 */
export async function applyLevelUpToCharacter(
  character: CharacterData,
  session: LevelUpSession
): Promise<CharacterData> {
  let updated = { ...character };

  // Step 0: Increase level
  updated = applyLevelIncrease(updated, session.toLevel);

  // Step 1: Apply tier achievements (if applicable)
  if (session.tierAchievement?.newExperience) {
    updated = applyTierAchievement(updated, session);
  }

  // Step 2: Apply advancement choices (async due to multiclass)
  updated = await applyAdvancements(updated, session);

  // Step 3: Apply damage threshold increases
  if (session.damageThresholdsIncreased) {
    updated = applyDamageThresholdIncrease(updated);
  }

  // Step 4: Apply domain card changes
  updated = applyDomainCardChanges(updated, session);

  // Step 5: Update tier advancement state
  updated = updateTierAdvancementState(updated, session);

  // Step 6: Add to level-up history
  updated = addToLevelUpHistory(updated, session);

  return updated;
}

// ============================================================================
// STEP 0: LEVEL INCREASE
// ============================================================================

/**
 * Increases character level and updates tier accordingly
 */
function applyLevelIncrease(
  character: CharacterData,
  newLevel: Level
): CharacterData {
  return {
    ...character,
    level: newLevel.toString(),
    tier: getTierForLevel(newLevel)
  };
}

// ============================================================================
// STEP 1: TIER ACHIEVEMENTS
// ============================================================================

/**
 * Applies tier achievement bonuses
 */
function applyTierAchievement(
  character: CharacterData,
  session: LevelUpSession
): CharacterData {
  if (!session.tierAchievement?.newExperience) return character;

  let updated = { ...character };

  // Add new experience at +2
  const newExperience = session.tierAchievement.newExperience;
  updated.experiences = [
    ...(character.experiences || []),
    newExperience
  ];

  // Increase proficiency by +1
  if (session.tierAchievement.proficiencyIncreased) {
    updated.proficiency = (character.proficiency || 1) + 1;
  }

  // Clear marked traits (levels 5 and 8)
  if (session.tierAchievement.traitsCleared) {
    updated.markedTraits = clearMarkedTraits();
  }

  return updated;
}

// ============================================================================
// STEP 2: ADVANCEMENTS
// ============================================================================

/**
 * Applies all advancement choices
 */
async function applyAdvancements(
  character: CharacterData,
  session: LevelUpSession
): Promise<CharacterData> {
  let updated = { ...character };

  for (const choice of session.advancementChoices) {
    updated = await applyAdvancementChoice(updated, choice, session.toLevel);
  }

  return updated;
}

/**
 * Applies a single advancement choice
 */
async function applyAdvancementChoice(
  character: CharacterData,
  choice: AdvancementChoice,
  currentLevel: Level
): Promise<CharacterData> {
  switch (choice.type) {
    case 'characterTraits':
      return applyCharacterTraitIncrease(character, choice.trait1, choice.trait2);

    case 'hitPoints':
      return applyHitPointIncrease(character);

    case 'stress':
      return applyStressIncrease(character);

    case 'experience':
      return applyExperienceIncrease(character, choice.experienceId);

    case 'domainCard':
      // Domain card from advancement choice (in addition to the one from Step 4)
      return addDomainCard(character, choice.cardId);

    case 'evasion':
      return applyEvasionIncrease(character);

    case 'subclassUpgrade':
      return applySubclassUpgrade(character, choice.cardId);

    case 'proficiency':
      return applyProficiencyIncrease(character);

    case 'multiclass':
      return await applyMulticlass(character, choice, currentLevel);

    default:
      return character;
  }
}

/**
 * Increases two character traits and marks them
 */
function applyCharacterTraitIncrease(
  character: CharacterData,
  trait1: string,
  trait2: string
): CharacterData {
  const updated = { ...character };

  // Increase trait values
  updated.traits = {
    ...character.traits,
    [trait1]: character.traits[trait1 as keyof typeof character.traits] + 1,
    [trait2]: character.traits[trait2 as keyof typeof character.traits] + 1
  };

  // Update baseStats.traits as well (for consistency)
  updated.baseStats = {
    ...character.baseStats,
    traits: {
      ...character.baseStats.traits,
      [trait1]: character.baseStats.traits[trait1 as keyof typeof character.baseStats.traits] + 1,
      [trait2]: character.baseStats.traits[trait2 as keyof typeof character.baseStats.traits] + 1
    }
  };

  // Mark the traits
  const currentMarkedTraits = character.markedTraits || createEmptyMarkedTraits();
  updated.markedTraits = markTraits(
    currentMarkedTraits,
    trait1 as any,
    trait2 as any
  );

  return updated;
}

/**
 * Increases hit point slots by 1
 */
function applyHitPointIncrease(character: CharacterData): CharacterData {
  return {
    ...character,
    hpSlots: character.hpSlots + 1,
    baseStats: {
      ...character.baseStats,
      hpSlots: character.baseStats.hpSlots + 1
    }
  };
}

/**
 * Increases stress slots by 1
 */
function applyStressIncrease(character: CharacterData): CharacterData {
  return {
    ...character,
    stressSlots: character.stressSlots + 1,
    baseStats: {
      ...character.baseStats,
      stressSlots: character.baseStats.stressSlots + 1
    }
  };
}

/**
 * Increases an existing experience by +1
 */
function applyExperienceIncrease(
  character: CharacterData,
  experienceId: string
): CharacterData {
  const experiences = character.experiences || [];
  const index = experiences.findIndex(e => e.id === experienceId);

  if (index === -1) {
    console.error(`Experience ${experienceId} not found`);
    return character;
  }

  const updatedExperiences = [...experiences];
  updatedExperiences[index] = increaseExperience(experiences[index]);

  return {
    ...character,
    experiences: updatedExperiences
  };
}

/**
 * Increases evasion by 1
 */
function applyEvasionIncrease(character: CharacterData): CharacterData {
  return {
    ...character,
    evasion: character.evasion + 1,
    baseStats: {
      ...character.baseStats,
      evasion: character.baseStats.evasion + 1
    }
  };
}

/**
 * Applies subclass upgrade (advances subclass card tier)
 */
function applySubclassUpgrade(
  character: CharacterData,
  cardId: string
): CharacterData {
  // Determine next tier based on card ID
  const currentTier = character.subclassCardTier || 'foundation';
  const nextTier: 'specialization' | 'mastery' | null =
    currentTier === 'foundation' ? 'specialization' :
    currentTier === 'specialization' ? 'mastery' :
    null;

  if (!nextTier) {
    // Already at mastery, no upgrade possible
    return character;
  }

  // Update subclass progression
  return {
    ...character,
    subclassCardTier: nextTier
  };
}

/**
 * Increases proficiency by 1
 */
function applyProficiencyIncrease(character: CharacterData): CharacterData {
  return {
    ...character,
    proficiency: (character.proficiency || 1) + 1
  };
}

/**
 * Applies multiclass (adds second class data)
 */
async function applyMulticlass(
  character: CharacterData,
  choice: Extract<AdvancementChoice, { type: 'multiclass' }>,
  acquiredAtLevel: Level
): Promise<CharacterData> {
  const tier = getTierForLevel(acquiredAtLevel);

  // Build complete multiclass data using the utility function
  const multiclassData = await buildMulticlassData(
    choice.classId,
    choice.domainId,
    choice.foundationCardId,
    acquiredAtLevel,
    tier
  );

  // Add foundation card to loadout
  const foundationCard: ContentReference = {
    contentId: choice.foundationCardId,
    contentType: 'domain_card' as const,
    quantity: 1
  };

  const currentLoadout = character.inventory?.domainCardLoadout || [];

  const updated: CharacterData = {
    ...character,
    multiclass: multiclassData,
    inventory: {
      ...character.inventory,
      domainCardLoadout: [...currentLoadout, foundationCard],
      domainCardVault: character.inventory?.domainCardVault || [],
      equipmentReferences: character.inventory?.equipmentReferences || [],
      manualItems: character.inventory?.manualItems || []
    }
  };

  return updated;
}

// ============================================================================
// STEP 3: DAMAGE THRESHOLDS
// ============================================================================

/**
 * Increases damage thresholds by +1 each
 */
function applyDamageThresholdIncrease(character: CharacterData): CharacterData {
  return {
    ...character,
    thresholds: {
      major: character.thresholds.major + 1,
      severe: character.thresholds.severe + 1
    },
    baseStats: {
      ...character.baseStats,
      thresholds: {
        major: character.baseStats.thresholds.major + 1,
        severe: character.baseStats.thresholds.severe + 1
      }
    }
  };
}

// ============================================================================
// STEP 4: DOMAIN CARDS
// ============================================================================

/**
 * Applies domain card acquisition and optional exchange
 */
function applyDomainCardChanges(
  character: CharacterData,
  session: LevelUpSession
): CharacterData {
  if (!session.newDomainCards || session.newDomainCards.length === 0) return character;

  let updated = { ...character };

  // Add all new domain cards
  for (const cardId of session.newDomainCards) {
    updated = addDomainCard(updated, cardId);
  }

  // Apply exchange if specified
  if (session.domainCardExchange) {
    updated = exchangeDomainCard(
      updated,
      session.domainCardExchange.oldCardId,
      session.domainCardExchange.newCardId
    );
  }

  return updated;
}

/**
 * Adds a new domain card to loadout (or vault if loadout full)
 */
function addDomainCard(
  character: CharacterData,
  cardId: string
): CharacterData {
  const newCard: ContentReference = {
    contentId: cardId,
    contentType: 'domain_card',
    quantity: 1
  };

  const currentLoadout = character.inventory?.domainCardLoadout || [];
  const currentVault = character.inventory?.domainCardVault || [];

  // Max loadout size is 5
  const addToLoadout = currentLoadout.length < 5;

  return {
    ...character,
    inventory: {
      ...character.inventory,
      domainCardLoadout: addToLoadout
        ? [...currentLoadout, newCard]
        : currentLoadout,
      domainCardVault: addToLoadout
        ? currentVault
        : [...currentVault, newCard],
      equipmentReferences: character.inventory?.equipmentReferences || [],
      manualItems: character.inventory?.manualItems || []
    }
  };
}

/**
 * Exchanges one domain card for another
 */
function exchangeDomainCard(
  character: CharacterData,
  oldCardId: string,
  newCardId: string
): CharacterData {
  const loadout = character.inventory?.domainCardLoadout || [];
  const vault = character.inventory?.domainCardVault || [];

  // Find old card in loadout
  const loadoutIndex = loadout.findIndex(c => c.contentId === oldCardId);
  if (loadoutIndex !== -1) {
    const newLoadout = [...loadout];
    newLoadout[loadoutIndex] = {
      contentId: newCardId,
      contentType: 'domain_card',
      quantity: 1
    };

    return {
      ...character,
      inventory: {
        ...character.inventory,
        domainCardLoadout: newLoadout,
        domainCardVault: vault,
        equipmentReferences: character.inventory?.equipmentReferences || [],
        manualItems: character.inventory?.manualItems || []
      }
    };
  }

  // Find old card in vault
  const vaultIndex = vault.findIndex(c => c.contentId === oldCardId);
  if (vaultIndex !== -1) {
    const newVault = [...vault];
    newVault[vaultIndex] = {
      contentId: newCardId,
      contentType: 'domain_card',
      quantity: 1
    };

    return {
      ...character,
      inventory: {
        ...character.inventory,
        domainCardLoadout: loadout,
        domainCardVault: newVault,
        equipmentReferences: character.inventory?.equipmentReferences || [],
        manualItems: character.inventory?.manualItems || []
      }
    };
  }

  // Card not found, just add new card
  console.warn(`Card ${oldCardId} not found for exchange`);
  return addDomainCard(character, newCardId);
}

// ============================================================================
// STEP 5: TIER ADVANCEMENT STATE
// ============================================================================

/**
 * Updates tier advancement state with marked slots
 */
function updateTierAdvancementState(
  character: CharacterData,
  session: LevelUpSession
): CharacterData {
  // Use the session's tier advancement state directly (already marked by wizard)
  const tierState = { ...session.tierAdvancementState };

  // Update crossed-out status
  const hasSubclassUpgrade = session.advancementChoices.some(c => c.type === 'subclassUpgrade');
  const hasMulticlass = session.advancementChoices.some(c => c.type === 'multiclass');

  if (hasSubclassUpgrade) {
    tierState.crossedOut.multiclass = true;
  }
  if (hasMulticlass) {
    tierState.crossedOut.subclassUpgrade = true;
  }

  // Update character's tier advancements
  return {
    ...character,
    tierAdvancements: {
      ...character.tierAdvancements,
      [session.tier]: tierState
    }
  };
}

// ============================================================================
// STEP 6: LEVEL-UP HISTORY
// ============================================================================

/**
 * Adds completed level-up to character history
 */
function addToLevelUpHistory(
  character: CharacterData,
  session: LevelUpSession
): CharacterData {
  const changes = extractLevelUpChanges(session);

  const historyEntry = {
    level: session.toLevel,
    sessionId: session.sessionId,
    completedAt: Date.now(),
    changes
  };

  return {
    ...character,
    levelUpHistory: [
      ...(character.levelUpHistory || []),
      historyEntry
    ]
  };
}

// ============================================================================
// ROLLBACK UTILITIES
// ============================================================================

/**
 * Checks if a level-up can be rolled back
 */
export function canRollbackLevelUp(character: CharacterData): boolean {
  const history = character.levelUpHistory || [];
  return history.length > 0;
}

/**
 * Gets the most recent level-up from history
 */
export function getLastLevelUp(character: CharacterData): {
  level: Level;
  sessionId: string;
  completedAt: number;
  changes: LevelUpChanges;
} | null {
  const history = character.levelUpHistory || [];
  return history.length > 0 ? history[history.length - 1] : null;
}

/**
 * Rolls back the most recent level-up (returns updated character)
 * WARNING: This is a destructive operation. Only use with DM approval.
 */
export function rollbackLastLevelUp(character: CharacterData): CharacterData {
  const lastLevelUp = getLastLevelUp(character);
  if (!lastLevelUp) {
    throw new Error('No level-up to roll back');
  }

  let updated = { ...character };

  // Decrease level
  const previousLevel = (lastLevelUp.level - 1) as Level;
  updated.level = previousLevel.toString();

  // Reverse tier achievements
  if (lastLevelUp.changes.tierAchievement) {
    updated = reverseTierAchievement(updated, lastLevelUp.changes);
  }

  // Reverse advancements
  updated = reverseAdvancements(updated, lastLevelUp.changes);

  // Reverse damage thresholds
  if (lastLevelUp.changes.damageThresholdsIncreased) {
    updated = reverseDamageThresholds(updated);
  }

  // Reverse domain card changes
  updated = reverseDomainCardChanges(updated, lastLevelUp.changes);

  // Remove from history
  updated.levelUpHistory = (character.levelUpHistory || []).slice(0, -1);

  return updated;
}

/**
 * Reverses tier achievement changes
 */
function reverseTierAchievement(
  character: CharacterData,
  changes: LevelUpChanges
): CharacterData {
  if (!changes.tierAchievement) return character;

  let updated = { ...character };

  // Remove the new experience
  const newExpId = changes.tierAchievement.newExperience.id;
  updated.experiences = (character.experiences || [])
    .filter(exp => exp.id !== newExpId);

  // Decrease proficiency
  if (changes.tierAchievement.proficiencyIncreased) {
    updated.proficiency = Math.max(1, (character.proficiency || 1) - 1);
  }

  // Note: We cannot restore marked traits state as it was cleared
  // This is a known limitation of rollback

  return updated;
}

/**
 * Reverses advancement changes
 */
function reverseAdvancements(
  character: CharacterData,
  changes: LevelUpChanges
): CharacterData {
  let updated = { ...character };
  const adv = changes.advancements;

  // Reverse character traits
  if (adv.characterTraitsIncreased) {
    const { trait1, trait2 } = adv.characterTraitsIncreased;
    updated.traits = {
      ...character.traits,
      [trait1]: character.traits[trait1 as keyof typeof character.traits] - 1,
      [trait2]: character.traits[trait2 as keyof typeof character.traits] - 1
    };
    updated.baseStats = {
      ...character.baseStats,
      traits: {
        ...character.baseStats.traits,
        [trait1]: character.baseStats.traits[trait1 as keyof typeof character.baseStats.traits] - 1,
        [trait2]: character.baseStats.traits[trait2 as keyof typeof character.baseStats.traits] - 1
      }
    };
  }

  // Reverse HP
  if (adv.hitPointsGained) {
    updated.hpSlots = character.hpSlots - adv.hitPointsGained;
    updated.baseStats = {
      ...character.baseStats,
      hpSlots: character.baseStats.hpSlots - adv.hitPointsGained
    };
  }

  // Reverse Stress
  if (adv.stressGained) {
    updated.stressSlots = character.stressSlots - adv.stressGained;
    updated.baseStats = {
      ...character.baseStats,
      stressSlots: character.baseStats.stressSlots - adv.stressGained
    };
  }

  // Reverse experience increase
  if (adv.experienceIncreased) {
    const experiences = character.experiences || [];
    const index = experiences.findIndex(e => e.id === adv.experienceIncreased);
    if (index !== -1) {
      const updatedExperiences = [...experiences];
      updatedExperiences[index] = {
        ...experiences[index],
        bonus: Math.max(1, experiences[index].bonus - 1)
      };
      updated.experiences = updatedExperiences;
    }
  }

  // Reverse evasion
  if (adv.evasionIncreased) {
    updated.evasion = character.evasion - 1;
    updated.baseStats = {
      ...character.baseStats,
      evasion: character.baseStats.evasion - 1
    };
  }

  // Reverse proficiency
  if (adv.proficiencyIncreased) {
    updated.proficiency = Math.max(1, (character.proficiency || 1) - 1);
  }

  return updated;
}

/**
 * Reverses damage threshold increases
 */
function reverseDamageThresholds(character: CharacterData): CharacterData {
  return {
    ...character,
    thresholds: {
      major: character.thresholds.major - 1,
      severe: character.thresholds.severe - 1
    },
    baseStats: {
      ...character.baseStats,
      thresholds: {
        major: character.baseStats.thresholds.major - 1,
        severe: character.baseStats.thresholds.severe - 1
      }
    }
  };
}

/**
 * Reverses domain card changes
 */
function reverseDomainCardChanges(
  character: CharacterData,
  changes: LevelUpChanges
): CharacterData {
  let loadout = character.inventory?.domainCardLoadout || [];
  let vault = character.inventory?.domainCardVault || [];

  // Remove new domain card
  const newCardId = changes.newDomainCard;
  loadout = loadout.filter(c => c.contentId !== newCardId);
  vault = vault.filter(c => c.contentId !== newCardId);

  // Reverse exchange if it happened
  if (changes.domainCardExchanged) {
    const { oldCardId, newCardId: exchangedCardId } = changes.domainCardExchanged;

    // Replace exchanged card back to original
    loadout = loadout.map(c =>
      c.contentId === exchangedCardId
        ? { ...c, contentId: oldCardId }
        : c
    );
    vault = vault.map(c =>
      c.contentId === exchangedCardId
        ? { ...c, contentId: oldCardId }
        : c
    );
  }

  return {
    ...character,
    inventory: {
      ...character.inventory,
      domainCardLoadout: loadout,
      domainCardVault: vault,
      equipmentReferences: character.inventory?.equipmentReferences || [],
      manualItems: character.inventory?.manualItems || []
    }
  };
}
